import * as Form from '@radix-ui/react-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LOGIN_FIELD_REQUIRED } from '@/constants/ui';
import React from 'react';
import { cn } from '@/lib/utils';

interface FormFieldProps {
  name: string;
  label: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string | null;
  type?: string;
  /** Extra attributes for the input (e.g. autoCapitalize, autoComplete). */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

/**
 * This component renders a form field with label and input control.
 * Integrates with Radix UI's Form components to manage input state and validation messages.
 * 
 * @param name - The name attribute for the input field.
 * @param label - The label displayed for the form field.
 * @param value - The current value of the input field.
 * @param onChange - Function called when the input value changes.
 * @param error - Optional. Error message to display if there's a validation error.
 * @param type - Optional. Type of the input field, defaults to 'text'.
 * @param inputProps - Optional. Extra attributes passed through to the input.
 */
const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  value,
  onChange,
  error,
  type = 'text',
  inputProps
}) => (
  <Form.Field name={name} serverInvalid={!!error}>
    <div className="flex items-center justify-between">
      <Form.Label asChild><Label>{label}</Label></Form.Label>
      <Form.Message className="text-sm font-semibold text-right text-red-600" match="valueMissing">
        {LOGIN_FIELD_REQUIRED(label)}
      </Form.Message>
    </div>
    <Form.Control asChild>
      <Input
        {...inputProps}
        name={name}
        value={value}
        onChange={onChange}
        className={cn("my-2", error && "border-red-500")} 
        required 
        type={type}
      />
    </Form.Control>
  </Form.Field>
);

export { FormField };
